import { useState } from 'react';
import {
  Table, Button, Space, Tag, Modal, Form, Input, InputNumber, message, Popconfirm, Card, Row, Col, Statistic, Drawer, List
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, TeamOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dealersApi } from '../api/dealers';
import type { Dealer, CreateDealerRequest, DealerFirm } from '../types';

export function Dealers() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDealer, setEditingDealer] = useState<Dealer | null>(null);
  const [selectedDealer, setSelectedDealer] = useState<Dealer | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();

  const { data: dealers = [], isLoading } = useQuery({
    queryKey: ['dealers'],
    queryFn: dealersApi.getAll,
  });

  const { data: dealerFirms = [] } = useQuery({
    queryKey: ['dealer-firms', selectedDealer?.id],
    queryFn: () => selectedDealer ? dealersApi.getFirms(selectedDealer.id) : Promise.resolve([]),
    enabled: !!selectedDealer,
  });

  const { data: dealerStats } = useQuery({
    queryKey: ['dealer-stats', selectedDealer?.id],
    queryFn: () => selectedDealer ? dealersApi.getStats(selectedDealer.id) : Promise.resolve(null),
    enabled: !!selectedDealer,
  });

  const createMutation = useMutation({
    mutationFn: dealersApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dealers'] });
      message.success('Bayi oluşturuldu');
      setIsModalOpen(false);
      form.resetFields();
    },
    onError: (error: any) => message.error(error.response?.data?.message || 'Bayi oluşturulamadı'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => dealersApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dealers'] });
      message.success('Bayi güncellendi');
      setIsModalOpen(false);
      setEditingDealer(null);
      form.resetFields();
    },
    onError: (error: any) => message.error(error.response?.data?.message || 'Bayi güncellenemedi'),
  });

  const deleteMutation = useMutation({
    mutationFn: dealersApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dealers'] });
      message.success('Bayi silindi');
    },
    onError: (error: any) => message.error(error.response?.data?.message || 'Bayi silinemedi'),
  });

  const handleSubmit = (values: CreateDealerRequest) => {
    if (editingDealer) {
      updateMutation.mutate({ id: editingDealer.id, data: { ...values, isActive: editingDealer.isActive } });
    } else {
      createMutation.mutate(values);
    }
  };

  const handleEdit = (dealer: Dealer) => {
    setEditingDealer(dealer);
    form.setFieldsValue(dealer);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingDealer(null);
    form.resetFields();
    form.setFieldsValue({ commissionRate: 0 });
    setIsModalOpen(true);
  };

  const handleViewDetails = (dealer: Dealer) => {
    setSelectedDealer(dealer);
    setIsDrawerOpen(true);
  };

  const getLicenseStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'green';
      case 'paused': return 'orange';
      case 'expired': return 'red';
      case 'deactivated': return 'default';
      default: return 'default';
    }
  };

  const getLicenseStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'Aktif';
      case 'paused': return 'Duraklatıldı';
      case 'expired': return 'Süresi Doldu';
      case 'deactivated': return 'Devre Dışı';
      default: return status;
    }
  };

  const columns = [
    {
      title: 'Bayi Adı',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: Dealer) => (
        <Space>
          {name}
          {!record.isActive && <Tag color="red">Pasif</Tag>}
        </Space>
      ),
    },
    {
      title: 'Yetkili',
      dataIndex: 'contactName',
      key: 'contactName',
    },
    {
      title: 'E-posta',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Telefon',
      dataIndex: 'phone',
      key: 'phone',
    },
    {
      title: 'Şehir',
      dataIndex: 'city',
      key: 'city',
    },
    {
      title: 'Firma Sayısı',
      dataIndex: 'firmCount',
      key: 'firmCount',
      render: (count: number) => (
        <Tag icon={<TeamOutlined />} color="blue">{count}</Tag>
      ),
    },
    {
      title: 'Komisyon',
      dataIndex: 'commissionRate',
      key: 'commissionRate',
      render: (rate: number) => `%${rate.toFixed(2)}`,
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: unknown, record: Dealer) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Bayiyi silmek istediğinize emin misiniz?"
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2>Bayiler</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Yeni Bayi
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={dealers}
        rowKey="id"
        loading={isLoading}
      />

      <Modal
        title={editingDealer ? 'Bayi Düzenle' : 'Yeni Bayi'}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingDealer(null);
          form.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Bayi Adı"
                rules={[{ required: true, message: 'Bayi adı gerekli' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="contactName"
                label="Yetkili Adı"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="email"
                label="E-posta"
                rules={[
                  { required: true, message: 'E-posta gerekli' },
                  { type: 'email', message: 'Geçerli bir e-posta girin' }
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="phone"
                label="Telefon"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="city"
                label="Şehir"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="district"
                label="İlçe"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="address"
            label="Adres"
          >
            <Input.TextArea rows={2} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="taxNumber"
                label="Vergi No"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="taxOffice"
                label="Vergi Dairesi"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="commissionRate"
            label="Komisyon Oranı (%)"
          >
            <InputNumber min={0} max={100} step={0.01} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="notes"
            label="Notlar"
          >
            <Input.TextArea rows={2} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {editingDealer ? 'Güncelle' : 'Oluştur'}
              </Button>
              <Button onClick={() => setIsModalOpen(false)}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Drawer
        title={selectedDealer?.name}
        open={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setSelectedDealer(null);
        }}
        width={600}
      >
        {selectedDealer && dealerStats && (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Card>
                  <Statistic title="Toplam Firma" value={dealerStats.totalFirms} />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic title="Aktif Firma" value={dealerStats.activeFirms} valueStyle={{ color: '#3f8600' }} />
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <Statistic title="30 Gün İçinde Biten" value={dealerStats.expiringIn30Days} valueStyle={{ color: '#faad14' }} />
                </Card>
              </Col>
            </Row>

            <h4>Firmalar</h4>
            <List
              dataSource={dealerFirms}
              renderItem={(firm: DealerFirm) => (
                <List.Item>
                  <List.Item.Meta
                    title={firm.name}
                    description={
                      <Space>
                        <code>{firm.subdomain}.raporgor.com</code>
                        <Tag color={getLicenseStatusColor(firm.licenseStatus)}>
                          {getLicenseStatusText(firm.licenseStatus)}
                        </Tag>
                        {firm.licenseExpiresAt && (
                          <span>
                            Bitiş: {new Date(firm.licenseExpiresAt).toLocaleDateString('tr-TR')}
                          </span>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
          </>
        )}
      </Drawer>
    </div>
  );
}
